<template>
  <sui-form-field>
    <sui-menu attached="top" color="blue">
      <a class="item" :class="{active: activeId === service.id}" v-for="service in selectedServices" :key="service.id" @click="openConfig(service.id)">
        <img :src="service.image" :alt="service.type" /> {{service.name}}
        <span data-inverted data-tooltip="Remove service">
          <i class="times fitted icon" @click.stop="deleteConfig(service.id)" />
        </span>
      </a>
      <sui-dropdown class="disabled" ref="dropdown" item text="No configurable services" v-show="!unselectedServices.length" />
      <sui-dropdown ref="dropdown" item text="Add service config" v-show="unselectedServices.length && isConfigLimitReached">
        <sui-dropdown-menu>
          <sui-dropdown-item disabled>Available only {{pluralize('service config', field.serviceConfigLimit, true)}}</sui-dropdown-item>
        </sui-dropdown-menu>
      </sui-dropdown>
      <sui-dropdown ref="dropdown" item text="Add service config" v-show="unselectedServices.length && !isConfigLimitReached">
        <sui-dropdown-menu>
          <sui-dropdown-header v-show="entity.project">Current project</sui-dropdown-header>
          <sui-dropdown-item v-for="service in currentServices" :key="service.id" :disabled="service.disabled" :image="{src: service.image}" :text="service.name" @click.prevent.stop="openGenerator(service.id)" />
          <sui-dropdown-item disabled v-show="entity.project && !currentServices.length">No configurable services</sui-dropdown-item>
          <sui-dropdown-header v-show="entity.project">Other projects</sui-dropdown-header>
          <sui-dropdown class="simple" selection item v-for="(projectServices, projectId) in otherServices" :key="projectId" icon="grey folder">
            <i class="dropdown icon" /> {{projectsById[projectId].name}}
            <sui-dropdown-menu class="simple">
              <sui-dropdown-item v-for="service in projectServices" :key="service.id" :disabled="service.disabled" :image="{src: service.image}" :text="service.name" @click.prevent.stop="openGenerator(service.id)" />
              <sui-dropdown-item disabled v-show="!projectServices.length">No configurable services</sui-dropdown-item>
            </sui-dropdown-menu>
          </sui-dropdown>
        </sui-dropdown-menu>
      </sui-dropdown>
    </sui-menu>
    <sui-segment attached="bottom" :color="field.error ? 'red' : 'green'">
      <div class="config readonly" v-if="!activeConfig">
        <codemirror ref="placeholder" :options="{readOnly: true}" @ready="onCmReady" />
      </div>
      <div class="config" :class="{readonly: previewing}" v-if="activeConfig">
        <sui-form-fields>
          <sui-form-field :width="13" :error="hasDomainsError" :required="activeConfig.https !== 'none'" v-if="configFileds.domains">
            <label>Domains</label>
            <sui-dropdown ref="domains" allowAdditions multiple search selection :maxSelections="hasD2CDomain ? 1 : Infinity" maxSelectionsMessage="Remove d2c domain if you want to use your own" v-model="$attrs.value[active].domains" @input="onInput()" />
            <small class="errors" v-if="activeError.domains">{{activeError.domains}}</small>
            <small>Use <a href="#" @click.prevent="useOwnDomains()">your own domains</a> or our one: <a href="#" @click.prevent="useD2CDomains()">{{d2cDomain}}</a></small>
          </sui-form-field>
          <sui-form-field :width="3" v-if="configFileds.sticky">
            <label>Sticky sessions</label>
            <sui-checkbox ref="sticky" toggle v-model="$attrs.value[active].sticky" @input="onInput()" />
            <span class="tooltip" data-inverted data-position="left center" :data-tooltip="'Sticky sessions can reduce amount of connections,\nand this is one of the ways to allow WebSockets work on multiple hosts'"><i class="warning sign yellow fitted icon" /></span>
          </sui-form-field>
          <sui-form-field :width="3" v-if="configFileds.static">
            <label>Serve static</label>
            <sui-checkbox ref="static" toggle v-model="$attrs.value[active].static" @input="onInput()" />
            <span class="tooltip" data-inverted data-position="left center"  data-tooltip="Nginx can serve the static only on the same host!"><i class="warning sign yellow fitted icon" /></span>
          </sui-form-field>
        </sui-form-fields>
        <sui-form-fields v-if="configFileds.https || configFileds.secure">
          <sui-form-field :width="8" v-if="configFileds.https">
            <label>HTTPS</label>
            <div class="ui mini fluid buttons">
              <div class="ui button" :class="{active: activeConfig.https === 'none'}" @click.prevent="setConfigField('https', 'none')">None</div>
              <div class="ui button" :class="{active: activeConfig.https === 'letsencrypt'}" @click.prevent="setConfigField('https', 'letsencrypt')">Let's encrypt</div>
              <div class="ui button" :class="{active: activeConfig.https === 'own'}" v-if="field.ownCertificates" @click.prevent="setConfigField('https', 'own')">Own certificates</div>
            </div>
          </sui-form-field>
          <sui-form-field :width="8" v-if="configFileds.secure">
            <label>Security level <span class="tooltip" data-inverted data-position="left center" :data-tooltip="'Minimum requirements for High security level:\nFirefox 27, Chrome 30, IE 11 on Windows 7, Edge, Opera 17, Safari 6, Android 4.4, and Java 8'"><i class="warning sign yellow fitted icon" /></span></label>
            <div class="ui mini fluid buttons">
              <div class="ui button" :class="{active: activeConfig.secure === 'medium', disabled: activeConfig.https === 'none'}" @click.prevent="setConfigField('secure', 'medium')">Medium (TLS 1.1, 1.2, 1.3)</div>
              <div class="ui button" :class="{active: activeConfig.secure === 'high', disabled: activeConfig.https === 'none'}" @click.prevent="setConfigField('secure', 'high')">High (TLS 1.2, 1.3)</div>
            </div>
          </sui-form-field>
        </sui-form-fields>
        <sui-form-fields v-if="activeConfig.https === 'own'">
          <sui-form-field :width="8" :error="hasCertError">
            <div class="ui mini fluid buttons">
              <sui-input class="mini fluid action" v-model="$attrs.value[active].cert" placeholder="HTTPS Certificate" @input="onInput()" />
              <label class="ui button"><input type="file" @change="uploadFile($event, 'cert')" />Upload</label>
            </div>
            <small class="errors" v-if="activeError.cert">{{activeError.cert}}</small>
          </sui-form-field>
          <sui-form-field :width="8" :error="hasKeyError">
            <div class="ui mini fluid buttons">
              <sui-input class="mini fluid action" v-model="$attrs.value[active].key" placeholder="HTTPS key" @input="onInput()" />
              <label class="ui button"><input type="file" @change="uploadFile($event, 'key')" />Upload</label>
            </div>
            <small class="errors" v-if="activeError.key">{{activeError.key}}</small>
          </sui-form-field>
        </sui-form-fields>
        <sui-segment class="fitted" basic>
          <codemirror ref="editor" v-model="$attrs.value[active][contentField]" :options="editorOptions" @cursorActivity="debouncedOnSelect()" v-if="!previewing" @ready="onCmReady" @focus.once="onFocus" />
          <codemirror ref="preview" :value="preview" :options="previewOptions" @cursorActivity="debouncedOnSelect()" v-if="previewing" @ready="onCmReady" />
          <div class="config-buttons">
            <div class="ui mini red icon button" data-inverted data-tooltip="Reset to default" v-show="!previewing" @mouseup.prevent="openGenerator(activeService.id)">
              <i class="refresh icon" />
            </div>
            <div class="ui mini orange icon button" :class="{disabled: !hasChanges}" data-inverted data-tooltip="Cancel current changes" v-show="!previewing" @click.prevent="cancelChanges()">
              <i class="undo icon" />
            </div>
            <div class="ui mini blue icon button" data-inverted data-tooltip="Preview config" v-show="!previewing" @mouseup.prevent="previewing = true" >
              <i class="eye icon" />
            </div>
            <div class="ui mini blue icon button" data-inverted data-tooltip="Edit config" v-show="previewing" @mouseup.prevent="previewing = false">
              <i class="pencil alternate icon" />
            </div>
          </div>
        </sui-segment>
        <sui-segment class="fitted" basic>
          <div class="ui labels">
            <sui-label class="link variable" size="small" v-for="(value, variable) in entityConfigVariables" :key="variable" data-inverted data-position="top left" :data-tooltip="value || '<empty>'">
              {{variable}}
            </sui-label>
          </div>
          <div class="ui labels">
            <sui-label class="link variable" size="small" v-for="(value, variable) in serviceConfigVariables" :key="variable" data-inverted data-position="top left" :data-tooltip="value || '<empty>'">
              {{variable}}
            </sui-label>
          </div>
        </sui-segment>
      </div>
      <sui-dimmer inverted active v-if="!activeConfig">
        No service configs
      </sui-dimmer>
      <sui-dimmer inverted :active="generating" v-if="generating">
        <sui-header>
          <Logo class="generator-logo" :entity="generator.service" vertical-align="middle" />{{generator.service.name}}
        </sui-header>
        <sui-form-field>
          <label>Mode</label>
          <div class="ui tiny fluid buttons">
            <div class="ui button" :class="{active: generator.mode === mode.value}" v-for="(mode, index) in field.serviceConfigModes" :key="index" @click.prevent="generator.mode = mode.value">
              {{mode.text}}
            </div>
          </div>
        </sui-form-field>
        <sui-form-fields>
          <sui-form-field :width="10">
            <div class="ui tiny blue fluid button" @click.prevent="generateConfig()">Generate config</div>
          </sui-form-field>
          <sui-form-field :width="6">
            <div class="ui tiny red basic fluid button" @click.prevent="closeGenerator()">Cancel</div>
          </sui-form-field>
        </sui-form-fields>
      </sui-dimmer>
    </sui-segment>
  </sui-form-field>
</template>

<script>
  import {mapGetters} from 'vuex'
  import groupBy from 'lodash/groupBy'
  import keyBy from 'lodash/keyBy'
  import omit from 'lodash/omit'
  import FormConfigs from '@/mixins/FormConfigs'
  import FormField from '@/mixins/FormField'
  import logos from '@/logos'

  export default {
    name: 'FormServiceServices',
    mixins: [
      FormConfigs,
      FormField,
    ],
    data() {
      return {
        generator: null,
      }
    },
    computed: {
      ...mapGetters(['account', 'config', 'projectsById', 'services', 'servicesById']),
      configFileds() {
        return keyBy(this.field.serviceConfigFields)
      },
      generating() {
        return Boolean(this.generator)
      },
      servicesData() {
        return this.services.map(service => {
          const hasUnsupportedProtocols = service.ports.some(port => !this.field.servicePortProtocols.includes(port.protocol))
          const hasMethod = service.constructor.methods[this.field.serviceMethod]
          return {
            id: service.id,
            image: logos.services[service.type],
            type: service.type,
            name: service.name,
            project: service.project,
            disabled: hasUnsupportedProtocols || !hasMethod,
          }
        })
      },
      selectedIds() {
        return this.$attrs.value.map(service => service.id).filter(serviceId => this.servicesById[serviceId])
      },
      unselectedServices() {
        return this.servicesData.filter(service => !this.selectedIds.includes(service.id) && service.id !== this.entity.id && this.servicesById[service.id])
      },
      servicesByProject() {
        const servicesByProject = groupBy(this.servicesData, service => service.project)
        for (const id in servicesByProject) {
          servicesByProject[id] = servicesByProject[id].filter(service => !this.selectedIds.includes(service.id) && service.id !== this.entity.id)
        }
        return servicesByProject
      },
      selectedServices() {
        return this.selectedIds.map(id => this.servicesData.find(service => service.id === id))
      },
      currentServices() {
        return this.entity.project ? this.servicesByProject[this.entity.project] || [] : []
      },
      otherServices() {
        return omit(this.servicesByProject, this.entity.project)
      },
      hasOtherServices() {
        return Boolean(Object.keys(this.otherServices).length)
      },
      isConfigLimitReached() {
        return this.field.serviceConfigLimit && (this.$attrs.value.length >= this.field.serviceConfigLimit)
      },
      activeService() {
        return this.activeConfig && this.servicesById[this.activeId]
      },
      d2cDomain() {
        return this.activeService ? `${this.activeService.name.toLowerCase()}${this.field.serviceConfigSuffix}.${this.account.shortId}.at.${this.config.domain}` : ''
      },
      hasD2CDomain() {
        return this.activeConfig ? this.activeConfig.domains.includes(this.d2cDomain) : false
      },
      hasDomainsError() {
        return Boolean(this.activeError.domains)
      },
      hasCertError() {
        return Boolean(this.activeError.cert)
      },
      hasKeyError() {
        return Boolean(this.activeError.key)
      },
    },
    methods: {
      onCmReady(codemirror) {
        setTimeout(() => {
          codemirror.refresh()
        }, 1000)
      },
      openGenerator(serviceId) {
        const service = this.servicesById[serviceId]
        const mode = this.entity.getServiceConfigMode(service)
        this.generator = {service, mode}
        this.$refs.dropdown.closeMenu()
      },
      generateConfig() {
        const {service, mode} = this.generator
        const existedIndex = this.$attrs.value.findIndex(config => config.id === service.id)
        if (existedIndex === -1) {
          const newConfig = this.entity.getServiceConfig(service, mode)
          this.$attrs.value.push(newConfig)
        } else {
          this.$attrs.value[existedIndex].config = this.entity.getServiceConfigText(service, mode)
          this.generator = null
          this.focus()
        }
        this.onInput()
      },
      closeGenerator() {
        this.generator = null
      },
      useOwnDomains() {
        if (this.activeConfig.domains.join('') === this.d2cDomain) {
          this.setConfigField('domains', [])
          this.onInput()
        }
        this.$refs.domains.$refs.search.focus()
      },
      useD2CDomains() {
        this.$attrs.value[this.active].domains = [this.d2cDomain]
        this.onInput()
      },
      uploadFile($event, field) {
        const fileReader = new FileReader()
        fileReader.addEventListener('load', event => {
          this.setConfigField(field, event.target.result)
        });
        fileReader.readAsText($event.target.files[0])
      },
    },
  }
</script>

<style scoped>
  .field { position: relative; }
  .tooltip { float: right; }
  .field > .tooltip { margin-top: .8em; }
  .dimmer { z-index: 10!important; }
  .menu { flex-wrap: wrap; border-bottom: none!important; background-color: #fafafa!important; }
  .menu .item { margin-left: -1px; margin-top: -1px; border-top: 1px solid #ddd; border-bottom: 1px solid #ddd; }
  .menu .item >>> img { margin-right: .3em!important; }
  .menu .item .times { margin-left: 1em!important; opacity: .3!important; cursor: pointer; }
  .menu .item .times:hover { opacity: 1!important; color: #c00; }
  .menu .item .input { margin: .2em 0!important; }
  .dropdown.simple .menu { top: 0!important; }
  .config-buttons { position: absolute; bottom: 1.3em; right: 1.2em; z-index: 2; }
  .generator-logo { margin: 0!important; }
  .variable:after { max-width: 200%; max-height: 400px; z-index: 1902; overflow: hidden; }
  .errors { display: none; color: #9f3a38; }
  .error .errors { display: block; }
</style>
